import React, { Suspense, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@emotion/react";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import LoadingView from "./components/elements/LoadingView";
import { unauthAxiosFetcher } from "./axios";
import { GlobalStateProvider } from "./customHooks/useGlobalState";
import { getUserById } from "./services/user";
import { convertIsoToExtended } from "./utils/multi-language";

const theme = createTheme({
  palette: {
    primary: {
      lighter: "#CCFFFD",
      light: "#66EEFF",
      main: "#00BFFF",
      dark: "#006FB7",
      darker: "#00397A",
      contrastText: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: "Manrope, sans-serif",
  },
});

// Function to get user's preferred language
const getUserPreferredLanguage = async () => {
  if (localStorage.getItem("USER_ID") && localStorage.getItem("JWT_TOKEN")) {
    try {
      const res = await getUserById(localStorage.getItem("USER_ID"));
      const user = res.data;
      if (user.preferred_language) {
        return user.preferred_language;
      }
    } catch (error) {
      console.error("Error fetching user preferred language:", error);
    }
  }
  return null;
};

// Component to load translations before rendering the app
const TranslationLoader = ({ children }) => {
  const [translationsLoaded, setTranslationsLoaded] = useState(false);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        // Try to get language from localStorage first, then user preferences, then browser settings
        const userPreferredLanguage = await getUserPreferredLanguage();
        const languageToUse =
          userPreferredLanguage ||
          convertIsoToExtended(
            navigator.language || navigator.userLanguage || "en"
          );

        // Only fetch translations for non-English languages
        if (languageToUse !== "en") {
          console.log(`Loading translations for ${languageToUse}...`);
          const response = await unauthAxiosFetcher(
            `/v1/translation/all/languages/${languageToUse}`
          );

          if (response && response.success) {
            const { data } = response;
            const allTranslations = {};
            Object.entries(data).forEach(([resource, translations]) => {
              allTranslations[resource] = translations;
            });
            window.__INITIAL_TRANSLATIONS__ = allTranslations;

            // Update localStorage with the used language
            if (!localStorage.getItem("locale")) {
              localStorage.setItem("locale", languageToUse);
            }
          }
        }

        setTranslationsLoaded(true);
      } catch (error) {
        console.error("Failed to load translations:", error);
        setTranslationsLoaded(true);
      }
    };

    loadTranslations();
  }, []);

  if (!translationsLoaded) {
    return <LoadingView />;
  }

  return children;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ThemeProvider theme={theme}>
    <TranslationLoader>
      <GlobalStateProvider>
        <Suspense fallback={<LoadingView />}>
          <App />
        </Suspense>
      </GlobalStateProvider>
    </TranslationLoader>
  </ThemeProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
